import React, { Component } from "react";
import { InjectedIntlProps, injectIntl } from "@ai360/core/intl";
import { connect } from "react-redux";

import { DialogBox, DialogBoxFooterType, NoLink, Section } from "~/core";
import { IRecordChildObject, IRecordObject } from "~/core/components/tree-view/tree-view";
import { actions as notificationActions } from "~/notifications";

import { messages } from "../i18n-messages";
import { CategoryTree } from "./category-tree/category-tree";

import "./report-page-selection.css";

interface IReportPageSelectionProps {
    isOpen: boolean;
    onChange: any;
    onClose: any;
    reportTypePages: any[];
    reportTypePagesPrefs: any;
    selectedReportTypes: any[];
    selectedReportTypePages: any;
    title: string;
}

interface IReportPageSelectionState {
    categories: Record<string, boolean>;
    dataSet: IRecordObject[];
    pagesDisplay: any;
    saveInvalid: boolean;
    treeKey: number;
}

// Prefixed so group nodes can never collide with (or be saved as) page ids
const reportNodeId = (rk) => `report:${rk}`;
const groupNodeId = (rk, groupName) => `group:${rk}:${groupName}`;
const pageNodeId = (rk, pk) => `page:${rk}:${pk}`;

class ReportPageSelection_ extends Component<
    IReportPageSelectionProps & InjectedIntlProps,
    IReportPageSelectionState
> {
    constructor(props) {
        super(props);

        this.state = {
            categories: {},
            dataSet: [],
            pagesDisplay: {},
            saveInvalid: false,
            treeKey: 0,
        };
    }

    _createReportTypePageDisplayObject(
        reportTypePages,
        selectedReportTypes,
        selectedReportTypePages
    ) {
        const pagesDisplay = reportTypePages.reduce((a, c) => {
            if (selectedReportTypes.indexOf(c.reportTypeName) > -1) {
                if (Object.keys(a).indexOf(c.reportTypeName) === -1) {
                    a[c.reportTypeName] = {
                        displayName: c.reportTypeDisplayName || c.reportTypeName,
                        pages: {},
                        show: true,
                    };
                }
                a[c.reportTypeName].pages[c.id] = {
                    displayName: c.displayName,
                    groupName: c.groupName,
                    show: Object.hasOwn(selectedReportTypePages, c.reportTypeName)
                        ? selectedReportTypePages[c.reportTypeName].indexOf(c.id) > -1
                        : true,
                };
            }
            return a;
        }, {});
        return pagesDisplay;
    }

    _onSave() {
        const { pagesDisplay } = this.state;
        const { selectedReportTypes } = this.props;

        const selectedReportTypePages = {};
        let saveInvalid = false;
        selectedReportTypes.forEach((st) => {
            if (
                Object.hasOwn(pagesDisplay, st) &&
                (!pagesDisplay[st].show ||
                    Object.keys(pagesDisplay[st].pages).every(
                        (pk) => !pagesDisplay[st].pages[pk].show
                    ))
            ) {
                saveInvalid = true;
                return;
            }
            if (
                Object.hasOwn(pagesDisplay, st) &&
                Object.keys(pagesDisplay[st].pages).some((pk) => !pagesDisplay[st].pages[pk].show)
            ) {
                selectedReportTypePages[st] = Object.keys(pagesDisplay[st].pages).filter(
                    (stpk) => pagesDisplay[st].pages[stpk].show
                );
            } else {
                selectedReportTypePages[st] = null;
            }
        });

        if (saveInvalid) {
            this.setState({ saveInvalid });
            return;
        }

        const reportTypePagesPrefs = {
            ...this.props.reportTypePagesPrefs,
            ...this.props.selectedReportTypePages,
            ...selectedReportTypePages,
        };
        Object.keys(selectedReportTypePages)
            .filter((k) => selectedReportTypePages[k] === null)
            .forEach((nullKey) => {
                delete reportTypePagesPrefs[nullKey];
            });
        this.props.onChange(reportTypePagesPrefs);
    }

    _onOpen() {
        const selectedReportTypePages = {
            ...this.props.reportTypePagesPrefs,
            ...this.props.selectedReportTypePages,
        };
        Object.keys(selectedReportTypePages)
            .filter((k) => !Object.hasOwn(this.props.selectedReportTypePages, k))
            .forEach((nullKey) => {
                delete selectedReportTypePages[nullKey];
            });
        this._resetState(selectedReportTypePages);
    }

    _createTree(pagesDisplay) {
        const categories: Record<string, boolean> = {};
        const dataSet: IRecordObject[] = Object.keys(pagesDisplay).map((rk) => {
            const { pages } = pagesDisplay[rk];
            const groups: Record<string, IRecordChildObject> = {};
            const children: IRecordChildObject[] = [];
            Object.keys(pages).forEach((pk) => {
                const { displayName, groupName, show } = pages[pk];
                const pageNode = { id: pageNodeId(rk, pk), name: displayName };
                categories[pageNode.id] = show;
                if (!groupName) {
                    children.push(pageNode);
                    return;
                }
                if (!groups[groupName]) {
                    groups[groupName] = {
                        children: [],
                        id: groupNodeId(rk, groupName),
                        isOpen: true,
                        name: groupName,
                    };
                    categories[groups[groupName].id] = false;
                    children.push(groups[groupName]);
                }
                groups[groupName].children.push(pageNode);
                categories[groups[groupName].id] = categories[groups[groupName].id] || show;
            });
            categories[reportNodeId(rk)] = pagesDisplay[rk].show;
            return {
                children,
                id: reportNodeId(rk),
                isOpen: true,
                name: pagesDisplay[rk].displayName,
            };
        });
        return { categories, dataSet };
    }

    _onTreeChange(categories) {
        const { pagesDisplay } = this.state;
        Object.keys(pagesDisplay).forEach((rk) => {
            pagesDisplay[rk].show = !!categories[reportNodeId(rk)];
            Object.keys(pagesDisplay[rk].pages).forEach((pk) => {
                pagesDisplay[rk].pages[pk].show = !!categories[pageNodeId(rk, pk)];
            });
        });
        this.setState({ pagesDisplay });
    }

    _resetState(selectedReportTypePages) {
        const pagesDisplay = this._createReportTypePageDisplayObject(
            this.props.reportTypePages,
            this.props.selectedReportTypes,
            selectedReportTypePages || {}
        );
        this.setState((prevState) => ({
            ...this._createTree(pagesDisplay),
            pagesDisplay,
            treeKey: prevState.treeKey + 1,
        }));
    }

    _resetToDefaults() {
        this._resetState(this.props.reportTypePagesPrefs);
    }

    render() {
        const { isOpen, letterIcon } = this.props;
        const { formatMessage } = this.props.intl;

        const { categories, dataSet, treeKey } = this.state;

        return (
            <React.Fragment>
                <DialogBox
                    className="report-page-selection-dialog"
                    draggable
                    isOpen={isOpen}
                    footerType={DialogBoxFooterType.NONE}
                    letterIcon={letterIcon}
                    onClose={() => this._onSave()}
                    onOpen={() => this._onOpen()}
                    title={this.props.title}
                    testId="report-page-selection-modal"
                >
                    <Section testId="report-page-selection-pages-section">
                        <CategoryTree
                            key={treeKey}
                            categories={categories}
                            categoryDataSet={dataSet}
                            onStatusChange={(values) => this._onTreeChange(values)}
                        />
                    </Section>
                    <Section testId="report-page-selection-reset-section">
                        <NoLink
                            className="reset"
                            label={formatMessage(messages.resetToDefault)}
                            onClick={() => {
                                this._resetToDefaults();
                            }}
                        />
                    </Section>
                </DialogBox>
                <DialogBox
                    isOpen={this.state.saveInvalid}
                    isModal={true}
                    draggable={true}
                    onClose={() => this.setState({ saveInvalid: false })}
                    title={formatMessage(messages.invalidPageSelection)}
                    footerType={DialogBoxFooterType.NONE}
                    testId="report-page-selection-invalid-modal"
                >
                    {formatMessage(messages.invalidPageSelectionMessage)}
                </DialogBox>
            </React.Fragment>
        );
    }
}

const mapDispatchToProps = (dispatch) => ({
    onApiError: (err, message) => dispatch(notificationActions.apiCallError(err, null, message)),
});

export const ReportPageSelection = connect<
    Record<string, unknown>,
    Record<string, unknown>,
    IReportPageSelectionProps
>(
    null,
    mapDispatchToProps
)(injectIntl(ReportPageSelection_));
